import { useQuery } from "@apollo/client/react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { configArray } from "components/Environment/configArray";
import EnvironmentContextProvider from "components/Environment/EnvironmentContext";
import EnvironmentPage from "components/Environment/view/EnvironmentPage";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useCanAddStackConfig from "shared/Stack/access/useCanAddStackConfig";
import useCanDeleteStackConfig from "shared/Stack/access/useCanDeleteStackConfig";
import { Stack } from "types/generated";

import StackHeader from "../components/Header";
import { StackContext } from "../Context";
import { getStacksBackUrl } from "../helpers";
import { GET_ENVIRONMENT } from "./gql";

const Environment = () => {
  const { stack: stackCtx } = useTypedContext(StackContext);

  const canCreateAndEditVariables = useCanAddStackConfig()(stackCtx);
  const canDeleteVariables = useCanDeleteStackConfig()(stackCtx);

  useTitle(`Environment · ${stackCtx.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stackCtx.name,
    },
  ]);

  const { error, loading, data } = useQuery<{ stack: Stack }>(GET_ENVIRONMENT, {
    variables: { stackId: stackCtx.id },
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  if (!data?.stack) {
    return <NotFoundPage />;
  }

  const stack = { ...stackCtx, ...data?.stack };

  const [localVariables, spaceliftVariables, mountedFiles] = configArray(stack.runtimeConfig);

  return (
    <>
      <StackHeader />
      <EnvironmentContextProvider
        resourceId={stack.id}
        refetchQueries={[GET_ENVIRONMENT]}
        canCreateAndEditVariables={canCreateAndEditVariables}
        canDeleteVariables={canDeleteVariables}
      >
        <EnvironmentPage
          localVariables={localVariables}
          mountedFiles={mountedFiles}
          spaceliftVariables={spaceliftVariables}
          attachedContexts={stack.attachedContexts}
        />
      </EnvironmentContextProvider>
    </>
  );
};

export default Environment;
